import { useEffect, useState } from "react";
import { Button, InlineAlert } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import useApi from "../../hooks/useApi";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type AdminCompanyActionsProps = {
    companyId: string;
}

function AdminCompanyActions({companyId}: AdminCompanyActionsProps) {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const api = useApi();
    const [data, setData] = useState<any>({});
    const [loading, setLoading] = useState(false);
    // Issue #884: getData/onLockChange previously had no catch at all, so a rejecting GET/write
    // (including the now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE notify-only codes -
    // #857 item 6) produced an unhandled rejection with no visible feedback. (onDelete below is
    // unreachable dead code - its trigger button is commented out and Confirm has no onClick -
    // left as-is rather than fixed, per #884 scope: "fix only real paths now affected".)
    const [loadError, setLoadError] = useState('');
    const [lockError, setLockError] = useState('');

    const getData = async () => {
        try {
            const result = await api.get(`/admin/company/${companyId}/summary`);
            setData(result.data);
            setLoadError('');
        } catch (err: any) {
            setLoadError(getApiErrorMessage(err, 'Failed to load this organisation\'s actions. Please try again.'));
        }
    }

    useEffect(() => {
        setLoading(true);
        getData();
        setLoading(false);
    }, [companyId])

    const onLockChange = async (locked: boolean) => {
        setLoading(true);
        setLockError('');
        try {
            await api.put(`/admin/company/${companyId}`, {
                company: {
                    adminLocked: locked,
                },
                _csrf: await getCSRFToken(),
            })
            await getData()
        } catch (err: any) {
            setLockError(getApiErrorMessage(err, 'Failed to update the lock status. Please try again.'));
            return;
        } finally {
            setLoading(false);
        }
    }

    const onDelete = async () => {
        setLoading(true);
        await api.post(`/admin/company/${companyId}/deleteData`, {});
        await getData();
        setLoading(false);
    }

    const isLocked = data?.company?.admin_locked;

    return (
        <div>
            <h3 className="text-white mb-3 mt-8">
                Organisation actions
            </h3>
            { loadError && <InlineAlert type='error' className='mb-4'>{ loadError }</InlineAlert> }
            { lockError && <InlineAlert type='error' className='mb-4'>{ lockError }</InlineAlert> }
            <div className="flex space-x-8">
                <div>
                    <Button
                        text={`${isLocked ? 'Unlock' : 'Lock'} organisation`}
                        type="Primary"
                        onClick={() => onLockChange(!isLocked)}
                        disabled={loading}
                    />
                </div>
                {/* <div>
                    <Button
                        text="Delete all data"
                        type="Primary"
                        hasLeadingIcon
                        icon="fa-solid fa-triangle-exclamation"
                        onClick={() => setIsDeleteModalOpen(true)}
                        disabled={loading}
                    />
                </div> */}
            </div>
            <CanonicalModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                size="small"
                title="Delete all data for [organisation name]?"
                actions={
                    <div className="flex space-x-4">
                        <Button type="Primary" text="Confirm" fullWidth />
                        <Button type="Outlined" text="Cancel" fullWidth onClick={() => setIsDeleteModalOpen(false)} />
                    </div>
                }
            >
                <div className="grow text-white mt-8">
                    This will delete the following:
                    <ul className="list-disc pl-6">
                        <li>Suppliers: { data.supplierCount }</li>
                        <li>Items: { data.itemCount }</li>
                        <li>Purchases: { data.purchaseCount } </li>
                        <li>Accounting periods { data.periodCount }</li>
                    </ul>
                </div>
            </CanonicalModal>
        </div>
    )
}

export default AdminCompanyActions;
